/* eslint-disable camelcase */
import moment from 'moment-timezone'
import { trackingLogger } from './logger'
import { Segment, SegmentAddRemoveProduct, SegmentProduct } from '@/types/tracking.types'
import { getAnonymousId, getAnonymousSegmentId } from '@/utils/ids.utils'
import { getPromoCodeList } from '@/utils/cart.utils'
import {
  AdmissionTypeValues,
  GoodRelationsPaymentMethod,
  PaymentMethodType,
  PaymentMethodTypeT,
} from '@/types/general.types'
import { USE_GTM, USE_SEGMENT } from '@/config/flags.config'
import {
  ADMISSION_TYPE_SELECTED,
  CHECKOUT_STARTED,
  DATE_TIME_SELECTED,
  EXPERIMENT_EXPOSURE,
  MW_ANON_ID,
  ORDER_COMPLETED,
  PAYMENT_INFO_ENTERED,
  PORTAL_PASS_BANNER_CLICKED,
  PORTAL_PASS_BANNER_VIEWED,
  PORTAL_PASS_MODAL_OPENED,
  PORTAL_PASS_MODAL_REDIRECT,
  PRODUCT_ADDED,
  PRODUCT_REMOVED,
  PRODUCT_VIEWED,
  SEGMENT_ANON_ID,
  SELLER_CHANGED,
  ZARAZ_UNAVAILABLE_SENTRY_SAMPLE_RATE,
} from '@/config/tracking.config'
import { APP_VERSION, GLOBAL_CONFIG, IS_PRODUCTION_DEPLOYMENT } from '@/config/environments.config'
import { TIME_FORMATS } from '@/config/dates.config'
import { sendGTMEvent } from '@next/third-parties/google'
import {
  loadSegmentWithConsent,
  type Segment as SegmentSdk,
  type ZarazUnavailableDetails,
} from '@meowwolf/cookie-consent-utils'
import * as Sentry from '@sentry/nextjs'

export type GtmDataLayerProps = {
  event?: string
  [x: string]: unknown
}

export const formatTicketsForTracking = (
  localLineItems: Transformed.LineItem[],
): Array<{
  ticket_type_id: string
  quantity: number
  event_session_id: string
}> => localLineItems.map((item) => ({
  ticket_type_id: item.tier.id,
  quantity: item.quantity,
  event_session_id: (item.timeslot as Transformed.Timeslot).id,
}))

/**
 * Get source value to pass to segment and GTM
 * if we have a mobile app cookie, consider the source to be `mobile-app`
 * otherwise `web`
 * @param {boolean} hasMobileAppCookie
 * @return {string} - source value
 */
export const getSource = (hasMobileAppCookie: boolean): string => (
  hasMobileAppCookie ? 'mobile-app' : 'web'
)

/**
 * Augment the payload with the anonymous id
 * @param {object | undefined} payload
 * @return {Promise<Record<string, unknown>>} - augmented payload
 */
export const augmentPayload = async (payload: object | undefined): Promise<Record<string, unknown>> => ({
  ...payload,
  [MW_ANON_ID]: getAnonymousId(),
  [SEGMENT_ANON_ID]: await getAnonymousSegmentId(),
})

const PaymentMethodTypeKeys = Object.keys(PaymentMethodType)

export const isPaymentMethodType = (typeA: string): typeA is PaymentMethodTypeT => PaymentMethodTypeKeys.includes(typeA)

/**
 * Transform functions for Segment
 */
export const transform = {
  segment: {
    sellerName: (sitePath: string, cityName: string): string => {
      const sellerType = sitePath.includes('events') ? 'Events' : 'Exhibition'
      return `${cityName} ${sellerType}`
    },
    /**
     * Get Good Relations payment method, or fallback to the incoming paymentMethod
     * @param {string} paymentMethod
     * @returns {string}
     */
    paymentMethod: (paymentMethod: string): string => (
      isPaymentMethodType(paymentMethod) ? GoodRelationsPaymentMethod[paymentMethod] : paymentMethod
    ),
    /**
     * Transform line items
     * @param {readonly Transformed.LineItem[]} items
     * @return {SegmentProduct[]}
     */
    lineItems: (items: readonly Transformed.LineItem[]): SegmentProduct[] => (
      items.map((item) => ({
        product_id: item.tier.id,
        sku: item.tier.id,
        name: item.tier.title,
        category: item.category.title,
        price: (item.amount / item.quantity),
        quantity: item.quantity,
        url: '', // not used
        image_url: '', // not used
      }))
    ),
    /**
     * Transform line items to products
     * @param {readonly Transformed.LineItem[]} items
     * @param {string} cartId
     * @param {string} url
     * @param {string} coupon
     * @param {AdmissionTypeValues} admissionType
     * @return {SegmentAddRemoveProduct[]}
     */
    lineItemsToProducts: (
      items: readonly Transformed.LineItem[],
      cartId: string,
      url: string,
      coupon: string,
      admissionType: AdmissionTypeValues,
    ): SegmentAddRemoveProduct[] => {
      const products = transform.segment.lineItems(items)

      return products.map((product) => ({
        ...product,
        cart_id: cartId,
        brand: 'Meow Wolf',
        variant: '', // not used
        position: 0, // not used
        coupon,
        url,
        admission_type: admissionType,
      }))
    },
  },
}

/**
 * Build the Segment `context` object shown in the tracking log.
 * `appVersionMiddleware` adds the same context to the events themselves.
 * @return {Record<string, unknown>} - segment context
 */
export const getSegmentContext = (): Record<string, unknown> => ({ app: { version: APP_VERSION } })

/**
 * Segment source middleware that adds the app version (Segment-canonical `context.app.version`) to every event,
 * including those the consent wrapper sends. Context passed as a call's properties or traits doesn't reach
 * `context`, so it has to be set here.
 * @param {Parameters<SegmentSdk.MiddlewareFunction>[0]} params - the event payload and the next middleware
 */
export const appVersionMiddleware: SegmentSdk.MiddlewareFunction = ({
  payload,
  next,
}: Parameters<SegmentSdk.MiddlewareFunction>[0]): void => {
  payload.obj.context = { ...payload.obj.context, app: { ...payload.obj.context?.app, version: APP_VERSION } }
  next(payload)
}

/**
 * Report a sample of visits where Zaraz (blocked or failed) never reported ready, so Segment loaded with
 * `JaAU: false`. cookie-consent-utils only logs this to the console, which Sentry doesn't capture.
 * @param {ZarazUnavailableDetails} details - how long Segment waited, and whether `window.zaraz` existed
 */
export const reportZarazUnavailable = (details: ZarazUnavailableDetails): void => {
  if (Math.random() >= ZARAZ_UNAVAILABLE_SENTRY_SAMPLE_RATE) return
  Sentry.captureMessage('Zaraz consent API not ready; Segment loaded with JaAU: false', {
    level: 'warning',
    fingerprint: ['zaraz-unavailable'],
    extra: details,
  })
}

/**
 * Get the Segment instance, creating it on first use.
 * Creating it here rather than in a mount effect means tracking doesn't depend on that effect running
 * before the caller's, and only one instance is ever made. `AnalyticsBrowser` buffers calls until Segment loads.
 * @return {Window['analytics'] | undefined} - Segment instance, or undefined when Segment is off
 */
export const getAnalytics = (): Window['analytics'] | undefined => {
  if (!USE_SEGMENT || typeof window === 'undefined') return undefined
  if (!window.analytics) {
    window.analytics = loadSegmentWithConsent(
      { writeKey: GLOBAL_CONFIG.SEGMENT_KEY },
      undefined,
      { onZarazUnavailable: reportZarazUnavailable },
    )
    window.analytics.addSourceMiddleware(appVersionMiddleware)
  }
  return window.analytics
}

/**
 * Segment tracking functions
 */
export const segment: Segment = {
  identify: (userIdOrTraits: string | object, traits?: object): void => {
    const context = getSegmentContext()
    trackingLogger('segment', 'identify', userIdOrTraits, { ...traits, context })
    getAnalytics()?.identify?.(userIdOrTraits, traits)
  },
  track: async (event, payload): Promise<void> => {
    const nextPayload = await augmentPayload(payload)
    const context = getSegmentContext()
    trackingLogger('segment', event, { ...nextPayload, context })
    if (!USE_SEGMENT) return
    getAnalytics()?.track?.(event, nextPayload)
  },
  /** Amplitude Experiment exposure, as the SDK built it — no `augmentPayload`, whose `anonymousId` is our session id, not Segment's. */
  exposure: (exposure): void => {
    trackingLogger('segment', EXPERIMENT_EXPOSURE, { ...exposure, context: getSegmentContext() })
    if (USE_SEGMENT) getAnalytics()?.track?.(EXPERIMENT_EXPOSURE, exposure)
  },
  page: (args): void => {
    const context = getSegmentContext()
    trackingLogger('segment', 'page', args, context)
    const analytics = getAnalytics()
    // Log an error if we are in prod and Segment is not available
    if (IS_PRODUCTION_DEPLOYMENT && !analytics) {
      // eslint-disable-next-line no-console
      console.error('Segment not available')
    }

    analytics?.page?.(args)
  },
  productAddedRemoved: async (event, payload): Promise<void> => {
    const nextPayload = await augmentPayload(payload)
    const context = getSegmentContext()
    trackingLogger('segment', event, { ...nextPayload, context })
    getAnalytics()?.track?.(event, nextPayload)
  },
  checkoutStarted: async (payload): Promise<void> => {
    const nextPayload = await augmentPayload(payload)
    const context = getSegmentContext()
    trackingLogger('segment', CHECKOUT_STARTED, { ...nextPayload, context })
    getAnalytics()?.track?.(CHECKOUT_STARTED, nextPayload)
  },
  paymentInfoEntered: async (payload): Promise<void> => {
    const nextPayload = await augmentPayload(payload)
    const context = getSegmentContext()
    trackingLogger('segment', PAYMENT_INFO_ENTERED, { ...nextPayload, context })
    getAnalytics()?.track?.(PAYMENT_INFO_ENTERED, nextPayload)
  },
  orderCompleted: async (payload): Promise<void> => {
    const nextPayload = await augmentPayload(payload)
    const context = getSegmentContext()
    trackingLogger('segment', ORDER_COMPLETED, { ...nextPayload, context })
    getAnalytics()?.track?.(ORDER_COMPLETED, nextPayload)
  },
  productViewed: async (payload): Promise<void> => {
    const nextPayload = await augmentPayload(payload)
    const context = getSegmentContext()
    trackingLogger('segment', PRODUCT_VIEWED, { ...nextPayload, context })
    getAnalytics()?.track?.(PRODUCT_VIEWED, nextPayload)
  },
  sellerChanged: (payload): void => {
    const context = getSegmentContext()
    trackingLogger('segment', SELLER_CHANGED, { ...payload, context })
    getAnalytics()?.track?.(SELLER_CHANGED, payload)
  },
  admissionTypeSelected: (payload): void => {
    const context = getSegmentContext()
    trackingLogger('segment', ADMISSION_TYPE_SELECTED, { ...payload, context })
    getAnalytics()?.track?.(ADMISSION_TYPE_SELECTED, payload)
  },
  portalPassModalOpened: (payload): void => {
    const context = getSegmentContext()
    trackingLogger('segment', PORTAL_PASS_MODAL_OPENED, { ...payload, context })
    getAnalytics()?.track?.(PORTAL_PASS_MODAL_OPENED, payload)
  },
  portalPassModalRedirect: (payload): void => {
    const context = getSegmentContext()
    trackingLogger('segment', PORTAL_PASS_MODAL_REDIRECT, { ...payload, context })
    getAnalytics()?.track?.(PORTAL_PASS_MODAL_REDIRECT, payload)
  },
  portalPassBannerViewed: (payload): void => {
    const context = getSegmentContext()
    trackingLogger('segment', PORTAL_PASS_BANNER_VIEWED, { ...payload, context })
    getAnalytics()?.track?.(PORTAL_PASS_BANNER_VIEWED, payload)
  },
  portalPassBannerClicked: (payload): void => {
    const context = getSegmentContext()
    trackingLogger('segment', PORTAL_PASS_BANNER_CLICKED, { ...payload, context })
    getAnalytics()?.track?.(PORTAL_PASS_BANNER_CLICKED, payload)
  },
}

export const gtm = {
  /**
   * Track event
   * @param event
   * @param args
   */
  track: ({
    event,
    ...args
  }: GtmDataLayerProps): void => {
    trackingLogger('gtm', event || 'track', args)
    if (USE_GTM) {
      sendGTMEvent({ event, ...args })
    }
  },
}

export const modifyCartTracking = ({
  localLineItems,
  lineItemsToDelete,
  cart,
  pathname,
  admissionType,
  origin,
}: {
  localLineItems: Transformed.LineItem[]
  lineItemsToDelete: Transformed.LineItem[]
  cart: Transformed.Cart
  pathname: string
  admissionType: AdmissionTypeValues
  origin: string
}): void => {
  // track addition of tickets
  transform.segment.lineItemsToProducts(
    localLineItems,
    cart.id,
    `${origin}${pathname}`,
    getPromoCodeList(cart).join(', '),
    admissionType,
  ).forEach((product) => {
    segment.productAddedRemoved(PRODUCT_ADDED, { ...product, admission_type: admissionType })
  })

  // track removal of tickets
  transform.segment.lineItemsToProducts(
    lineItemsToDelete,
    cart.id,
    `${origin}${pathname}`,
    getPromoCodeList(cart).join(', '),
    admissionType,
  ).forEach((product) => {
    segment.productAddedRemoved(PRODUCT_REMOVED, { ...product, admission_type: admissionType })
  })

  if (localLineItems.length) {
    gtm.track({
      event: 'Tickets Selected',
      url: `${origin}${pathname}`,
      path: pathname,
      tickets: formatTicketsForTracking(localLineItems),
    })
  }
}
/**
 * Track date and time selection via Segment and GTM
 */

export const handleDateTimeTracking = (startTime: string, timeZone: string): void => {
  const date = moment.tz(startTime, timeZone).format(TIME_FORMATS.calendarDate)
  const time = moment.tz(startTime, timeZone).format(TIME_FORMATS.timeWithAMPM)

  segment.track(DATE_TIME_SELECTED, { date, time })
  gtm.track({ event: DATE_TIME_SELECTED, date, time })
}
